import { ClusterCluster, OrgOrganization } from '@clinic/client'

export type StatementRouteDecision = 'denied' | 'pending' | 'v1' | 'v2'

export type StatementRouteReason =
  | 'access_denied'
  | 'cluster_ineligible'
  | 'probe_pending'
  | 'probe_error'
  | 'probe_unavailable'
  | 'v1_datasource'
  | 'v2_datasource'

export interface StatementRouteResolution {
  decision: StatementRouteDecision
  reason: StatementRouteReason
}

interface ResolveStatementRouteInput {
  internal: boolean
  eligible: boolean
  probeLoading: boolean
  probeError: boolean
  v2Available?: boolean
}

/** Pure route decision shared by the entry resolver, direct-route guard and tests. */
export function resolveStatementRoute({
  internal,
  eligible,
  probeLoading,
  probeError,
  v2Available
}: ResolveStatementRouteInput): StatementRouteResolution {
  if (!internal) {
    return { decision: 'denied', reason: 'access_denied' }
  }
  if (!eligible) {
    return { decision: 'v1', reason: 'cluster_ineligible' }
  }
  if (probeLoading) {
    return { decision: 'pending', reason: 'probe_pending' }
  }
  if (probeError) {
    return { decision: 'v1', reason: 'probe_error' }
  }
  if (v2Available === true) {
    return { decision: 'v2', reason: 'v2_datasource' }
  }
  if (v2Available === false) {
    return { decision: 'v1', reason: 'v1_datasource' }
  }
  return { decision: 'v1', reason: 'probe_unavailable' }
}

export function normalizeRouteSearch(search: string): string {
  if (!search) return ''
  return search.startsWith('?') ? search : `?${search}`
}

export function buildClinicStatementPath(
  orgId: string,
  clusterId: string,
  version: 'resolver' | 'v2' | 'v2-detail',
  search = ''
): string {
  const suffix =
    version === 'resolver'
      ? 'sql-statement'
      : version === 'v2'
      ? 'sql-statement-v2'
      : 'sql-statement-v2/detail'
  return `/orgs/${orgId}/clusters/${clusterId}/${suffix}${normalizeRouteSearch(
    search
  )}`
}

/**
 * Preserve the router query verbatim after the embedded dashboard hash. This
 * keeps from/to/search and any future Statement V1 parameters intact.
 */
export function buildStatementV1DashboardUrl(
  cluster: ClusterCluster,
  org: OrgOrganization,
  search = ''
): string {
  const dashboardParams = new URLSearchParams()
  dashboardParams.set('provider', cluster.provider || '')
  dashboardParams.set('region', cluster.region || '')
  dashboardParams.set('orgName', org.name || '')
  dashboardParams.set('orgId', org.externalID || '')
  dashboardParams.set('projectId', cluster.projectID || '')
  dashboardParams.set('clusterId', cluster.id || '')
  dashboardParams.set('clusterName', cluster.name || '')
  dashboardParams.set('deployType', cluster.deployType || '')
  dashboardParams.set('version', cluster.version || '')

  return `dashboard/cloud/ngm.html?${dashboardParams.toString()}#/statement${normalizeRouteSearch(
    search
  )}`
}
